import React, { useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import { useTranslation } from 'i18n-web/i18next'
import CloseButton from 'components/Atoms/CloseButton'
import Text from 'components/Atoms/Text'

const OpenButton = styled.button`
  position: absolute;
  top: 0;
  right: 20px;
  padding: 0 12px;
  height: 36px;
  font-size: ${props => props.theme.font.size.mobile.small};
  font-weight: 700;
  line-height: 1;
  color: ${props => props.theme.palette.white};
  background-color: ${props => props.theme.palette.primary};
  border: none;
  border-radius: 0 0 4px 4px;
  appearance: none;

  @media (min-width: ${props => props.theme.flexboxgrid.breakpoints.sm}rem) {
    display: none;
  }
`

const Component = styled.div`
  background-color: ${props => props.theme.palette.primary};

  .h4 {
    padding-bottom: 8px;
    margin-bottom: 25px;
    font-size: ${props => props.theme.font.size.mobile.medium};
    font-weight: bold;
    text-transform: uppercase;
    color: ${props => props.theme.palette.grayDark};
    border-bottom: solid 3px ${props => props.theme.palette.grayDark};

    @media (min-width: ${props => props.theme.flexboxgrid.breakpoints.sm}rem) {
      margin-bottom: 40px;
      font-size: ${props => props.theme.font.size.desktop.h4};
      color: ${props => props.theme.palette.primary};
      border: none;
    }
  }

  .closeButton {
    position: fixed;
    top: 10px;
    right: 10px;

    @media (min-width: ${props => props.theme.flexboxgrid.breakpoints.sm}rem) {
      display: none;
    }
  }

  @media (min-width: ${props => props.theme.flexboxgrid.breakpoints.sm}rem) {
    background: none;

    &.active {
      display: none;
    }
  }

  @media (max-width: ${props => props.theme.flexboxgrid.breakpoints.sm}rem) {
    display: none;

    &.active {
      display: block;
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      padding: 60px 20px 0;
      z-index: 100;
    }
  }
`

const ListItem = styled.li`
  display: flex;

  & + li {
    margin-top: 24px;
  }
`

const Icon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  min-width: 60px;
  height: 60px;
  margin-right: 20px;
  background-color: ${props => props.theme.palette.grayDark};
  border-radius: 50%;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.35);

  img {
    width: auto;
    height: 35px;

    &.small {
      height: 25px;
    }

    &.medium {
      height: 30px;
    }
  }
`

const ListItemTitle = styled(Text)`
  font-size: ${props => props.theme.font.size.mobile.small};
  font-weight: bold;
  color: ${props => props.theme.palette.white};

  @media (min-width: ${props => props.theme.flexboxgrid.breakpoints.sm}rem) {
    font-size: ${props => props.theme.font.size.desktop.regular};
    color: ${props => props.theme.palette.primary};
  }
`

const ListItemSubtitle = styled(Text)`
  font-size: ${props => props.theme.font.size.mobile.small};
  color: ${props => props.theme.palette.grayDark};

  @media (min-width: ${props => props.theme.flexboxgrid.breakpoints.sm}rem) {
    font-size: ${props => props.theme.font.size.desktop.regular};
  }
`

const Benefits = ({ isMobile }) => {
  const { t } = useTranslation()
  const [isActive, setActive] = useState(false)

  if (isMobile && !isActive) {
    return (
      <OpenButton onClick={() => setActive(true)}>
        {t('signUp.benefits.button')}
      </OpenButton>
    )
  }

  return (
    <Component className={isActive ? 'active' : ''}>
      <CloseButton className="closeButton" onClick={() => setActive(false)} />
      <Text as="h4" className="h4">
        {t('common.benefits.title')}
      </Text>
      <ul>
        <ListItem>
          <Icon>
            <img src={'/static/icons/icon-credits.svg'} />
          </Icon>
          <div>
            <ListItemTitle>{t('common.benefits.benefit1.title')}</ListItemTitle>
            <ListItemSubtitle>
              {t('common.benefits.benefit1.description')}
            </ListItemSubtitle>
          </div>
        </ListItem>
        <ListItem>
          <Icon>
            <img
              className="medium"
              src={'/static/icons/icon-more-spaces.svg'}
            />
          </Icon>
          <div>
            <ListItemTitle>{t('common.benefits.benefit2.title')}</ListItemTitle>
            <ListItemSubtitle>
              {t('common.benefits.benefit2.description')}
            </ListItemSubtitle>
          </div>
        </ListItem>
        <ListItem>
          <Icon>
            <img className="medium" src={'/static/icons/icon-directions.svg'} />
          </Icon>
          <div>
            <ListItemTitle>{t('common.benefits.benefit3.title')}</ListItemTitle>
            <ListItemSubtitle>
              {t('common.benefits.benefit3.description')}
            </ListItemSubtitle>
          </div>
        </ListItem>
        <ListItem>
          <Icon>
            <img className="small" src={'/static/icons/icon-events.svg'} />
          </Icon>
          <div>
            <ListItemTitle>{t('common.benefits.benefit4.title')}</ListItemTitle>
            <ListItemSubtitle>
              {t('common.benefits.benefit4.description')}
            </ListItemSubtitle>
          </div>
        </ListItem>
        <ListItem>
          <Icon>
            <img className="medium" src={'/static/icons/icon-validate.svg'} />
          </Icon>
          <div>
            <ListItemTitle>{t('common.benefits.benefit5.title')}</ListItemTitle>
            <ListItemSubtitle>
              {t('common.benefits.benefit5.description')}
            </ListItemSubtitle>
          </div>
        </ListItem>
        <ListItem>
          <Icon>
            <img
              className="small"
              src={'/static/icons/icon-customer-support.svg'}
            />
          </Icon>
          <div>
            <ListItemTitle>{t('common.benefits.benefit6.title')}</ListItemTitle>
            <ListItemSubtitle>
              {t('common.benefits.benefit6.description')}
            </ListItemSubtitle>
          </div>
        </ListItem>
        <ListItem>
          <Icon>
            <img src={'/static/icons/icon-credits.svg'} />
          </Icon>
          <div>
            <ListItemTitle>{t('common.benefits.benefit7.title')}</ListItemTitle>
            <ListItemSubtitle>
              {t('common.benefits.benefit7.description')}
            </ListItemSubtitle>
          </div>
        </ListItem>
      </ul>
    </Component>
  )
}

Benefits.propTypes = {
  isMobile: PropTypes.bool,
}

export default Benefits
